import React, { useEffect, useState } from "react";
import { Select } from "antd";
import {
  clearMockScenario,
  isMockAllowed,
  resolveMockScenario,
  setMockScenario,
  MOCK_SCENARIO_CHANGE_EVENT,
} from "./mock-mode";
import {
  DEFAULT_MOCK_SCENARIO_ID,
  MOCK_SCENARIOS,
  MockScenarioId,
} from "./scenarios";

type DevScenarioPanelProps = {
  onScenarioChange?: () => void;
};

export function DevScenarioPanel({ onScenarioChange }: DevScenarioPanelProps) {
  const [scenarioId, setScenarioId] = useState<MockScenarioId | null>(
    () => resolveMockScenario()?.id ?? null,
  );

  useEffect(() => {
    if (!isMockAllowed()) return;

    const sync = () => {
      setScenarioId(resolveMockScenario()?.id ?? null);
    };

    window.addEventListener(MOCK_SCENARIO_CHANGE_EVENT, sync);
    window.addEventListener("popstate", sync);
    return () => {
      window.removeEventListener(MOCK_SCENARIO_CHANGE_EVENT, sync);
      window.removeEventListener("popstate", sync);
    };
  }, []);

  if (!isMockAllowed()) {
    return null;
  }

  const activate = (id: MockScenarioId) => {
    const wasActive = scenarioId !== null;
    setMockScenario(id);
    setScenarioId(id);
    // Boot path (AuthProvider vs not) is decided at load time — reload when entering mock.
    if (!wasActive) {
      window.location.reload();
      return;
    }
    onScenarioChange?.();
  };

  const inactive = scenarioId === null;

  return (
    <div
      className={
        "DevScenarioPanel" + (inactive ? " DevScenarioPanel--inactive" : "")
      }
      role="region"
      aria-label="Mock scenario"
    >
      <span className="DevScenarioPanel-badge">
        {inactive ? "Dev" : "Mock"}
      </span>
      {inactive ? (
        <>
          <button
            type="button"
            className="DevScenarioPanel-enable"
            onClick={() => activate(DEFAULT_MOCK_SCENARIO_ID)}
          >
            Enable mock
          </button>
          <Select
            className="DevScenarioPanel-select"
            size="small"
            placeholder="Or pick scenario…"
            value={undefined}
            options={MOCK_SCENARIOS.map((s) => ({
              value: s.id,
              label: s.label,
            }))}
            onChange={(value) => {
              if (value) activate(value as MockScenarioId);
            }}
            popupMatchSelectWidth={false}
            aria-label="Enable mock scenario"
          />
        </>
      ) : (
        <>
          <Select
            className="DevScenarioPanel-select"
            size="small"
            value={scenarioId}
            options={MOCK_SCENARIOS.map((s) => ({
              value: s.id,
              label: s.label,
            }))}
            onChange={(value) => {
              if (value) activate(value as MockScenarioId);
            }}
            popupMatchSelectWidth={false}
            aria-label="Mock scenario"
          />
          <button
            type="button"
            className="DevScenarioPanel-exit"
            onClick={() => {
              clearMockScenario();
              setScenarioId(null);
              window.location.reload();
            }}
          >
            Exit
          </button>
        </>
      )}
    </div>
  );
}
